import { Container, Sprite, Texture } from '../../../odie';

import { CandleBlockProps } from '../../defs/block';
import { blockPositionView } from '../../util/blockTools';

// constants
//-----------------------------------------------------------------------------
const manifest = {
  base: 'candles/block.candles.png',
  base9: 'candles/block.candles.9.png',
  candle: 'candles/scrap.candle.png',
};

const positions = {
  candlesUpTo7: [
    {
      x: 8,
      y: 89,
      scaleY: 1,
    },
    {
      x: 41,
      y: 89,
      scaleY: 1,
    },
    {
      x: 74,
      y: 89,
      scaleY: 1,
    },
    {
      x: 107,
      y: 89,
      scaleY: 1,
    },
    {
      x: 24,
      y: 36,
      scaleY: 1,
    },
    {
      x: 57,
      y: 36,
      scaleY: 1,
    },
    {
      x: 90,
      y: 36,
      scaleY: 1,
    },
  ],
  candles8: [
    {
      x: 8,
      y: 89,
      scaleY: 1,
    },
    {
      x: 41,
      y: 89,
      scaleY: 1,
    },
    {
      x: 74,
      y: 89,
      scaleY: 1,
    },
    {
      x: 107,
      y: 89,
      scaleY: 1,
    },
    {
      x: 8,
      y: 36,
      scaleY: 1,
    },
    {
      x: 41,
      y: 36,
      scaleY: 1,
    },
    {
      x: 74,
      y: 36,
      scaleY: 1,
    },
    {
      x: 107,
      y: 36,
      scaleY: 1,
    },
  ],
  candles9: [
    {
      x: 8,
      y: 108,
      scaleY: 0.7,
    },
    {
      x: 41,
      y: 108,
      scaleY: 0.7,
    },
    {
      x: 74,
      y: 108,
      scaleY: 0.7,
    },
    {
      x: 107,
      y: 108,
      scaleY: 0.7,
    },
    {
      x: 24,
      y: 70,
      scaleY: 0.7,
    },
    {
      x: 57,
      y: 70,
      scaleY: 0.7,
    },
    {
      x: 90,
      y: 70,
      scaleY: 0.7,
    },
    {
      x: 41,
      y: 33,
      scaleY: 0.7,
    },
    {
      x: 74,
      y: 33,
      scaleY: 0.7,
    },
  ],
};

function getPositionsByNumCandles(numCandles: number) {
  const { candlesUpTo7, candles8, candles9 } = positions;

  if (numCandles <= 7) {
    return candlesUpTo7;
  }

  if (numCandles === 8) {
    return candles8;
  }

  if (numCandles === 9) {
    return candles9;
  }

  return null;
}

/*
    candle block view
*/
export class CandleBlockView extends Container {
  // fields
  //-------------------------------------------------------------------------
  // constants
  public static LIMIT = positions.candles9.length;
  // scene
  private _candles: (Sprite | undefined)[] = [];
  // state
  private _count = 0;
  private bg: Sprite;

  // properties
  //-------------------------------------------------------------------------
  public get count(): number {
    return this._count;
  }

  public set count(value: number) {
    const oldCount = this._count;
    // update field
    this._count = value;

    // if adding
    if (value > oldCount) {
      // spawn candles
      for (let i = oldCount; i < value; ++i) {
        this._spawnCandle(i);
      }

      // Set appropriate bg.
      this.bg.texture = Texture.from(
        value < 9 ? manifest.base : manifest.base9,
      );
      this.bg.width = 152;
      this.bg.height = 152;

      // else removing
    } else {
      // despawn candles
      for (let i = oldCount - 1; i >= value; --i) {
        this._despawnCandle(i);
      }
    }
    this.sortChildren();
  }

  // init
  //-------------------------------------------------------------------------
  constructor(props: CandleBlockProps) {
    super();

    // create bg
    this.bg = Sprite.from(manifest.base);
    this.addChild(this.bg);

    // complete scene
    blockPositionView(this, props);

    this.bg.x = -76;
    this.bg.y = -75;
  }

  // private: spawn
  //-------------------------------------------------------------------------
  private _spawnCandle(index: number) {
    const positions = getPositionsByNumCandles(this._count);
    if (!positions) throw new Error(`Invalid candles count: ${this._count}`);

    // create candle sprite
    const candle = Sprite.from(manifest.candle);

    const isNine = this._count === 9;
    const scale = isNine ? 0.8 : 0.9;
    candle.width = 33 * scale;
    candle.height = 47 * scale;
    const offsetX = isNine ? 22 : 17;
    const offsetY = isNine ? 83 : 79;
    const lineOffset = isNine ? -42 : -50;

    const { candlesOnLine, line, lineIndex } = this.getCandleLine(index);
    const extraSpace = ((4 - candlesOnLine) * candle.width) / 2;
    candle.x = offsetX + extraSpace + candle.width * lineIndex + this.bg.x;
    candle.y = offsetY + lineOffset * line + this.bg.y + 13;

    // add to table
    this._candles[index] = candle;

    // spawn
    this.addChild(candle);
  }

  private _despawnCandle(index: number) {
    const sprite = this._candles[index];

    // despawn
    if (sprite) this.removeChild(sprite);

    // remove from table
    this._candles[index] = undefined;
  }

  public static assets(): string[] {
    return [manifest.base, manifest.base9, manifest.candle];
  }

  private maxCandlePerLine(index: number): number {
    if (this._count === 9) {
      return index < 4 ? 4 : index < 7 ? 3 : 2;
    }
    return this._count < 5 ? 2 : this._count < 7 ? 3 : 4;
  }

  private getCandleLine(index: number) {
    let line = 0;
    let lineStart = 0;
    let maxCandlesPerLine = this.maxCandlePerLine(0);
    while (index >= lineStart + maxCandlesPerLine) {
      lineStart += maxCandlesPerLine;
      maxCandlesPerLine = this.maxCandlePerLine(lineStart);
      line++;
    }

    return {
      line,
      maxCandlesPerLine,
      candlesOnLine: Math.min(maxCandlesPerLine, this._count - lineStart),
      lineIndex: index - lineStart,
    };
  }

  public getNextCandle() {
    const index = this._count - 1;
    return this._candles[index];
  }
}
